/**
 * ShreemTech Universal Admin Panel
 * Copyright (c) 2026 ShreemTech Ltd. All Rights Reserved.
 * PROPRIETARY AND CONFIDENTIAL — TRADE SECRET
 */

"use client";

import { useEffect } from "react";
import platformTheme from "@/config/themes/platform.json";

interface ThemeColors {
  primary: string;
  primary_light: string;
  primary_dark: string;
  accent: string;
  warning: string;
  danger: string;
  background: string;
  surface: string;
  text_primary: string;
  text_secondary: string;
  sidebar_bg: string;
  sidebar_text: string;
  sidebar_active: string;
  header_bg: string;
  header_text: string;
}

interface ProductTheme {
  product_id: string;
  colors: ThemeColors;
  layout: {
    border_radius: number;
    sidebar_width: number;
    sidebar_collapsed_width: number;
    header_height: number;
  };
  typography: {
    font_family: string;
  };
}

function injectCssVariables(theme: ProductTheme) {
  const root = document.documentElement;
  const { colors, layout, typography } = theme;

  root.style.setProperty("--color-primary", colors.primary);
  root.style.setProperty("--color-primary-light", colors.primary_light);
  root.style.setProperty("--color-primary-dark", colors.primary_dark);
  root.style.setProperty("--color-accent", colors.accent);
  root.style.setProperty("--color-warning", colors.warning);
  root.style.setProperty("--color-danger", colors.danger);
  root.style.setProperty("--color-background", colors.background);
  root.style.setProperty("--color-surface", colors.surface);
  root.style.setProperty("--color-text-primary", colors.text_primary);
  root.style.setProperty("--color-text-secondary", colors.text_secondary);
  root.style.setProperty("--color-sidebar-bg", colors.sidebar_bg);
  root.style.setProperty("--color-sidebar-text", colors.sidebar_text);
  root.style.setProperty("--color-sidebar-active", colors.sidebar_active);
  root.style.setProperty("--color-header-bg", colors.header_bg);
  root.style.setProperty("--color-header-text", colors.header_text);
  root.style.setProperty("--border-radius", `${layout.border_radius}px`);
  root.style.setProperty("--sidebar-width", `${layout.sidebar_width}px`);
  root.style.setProperty("--sidebar-collapsed-width", `${layout.sidebar_collapsed_width}px`);
  root.style.setProperty("--header-height", `${layout.header_height}px`);
  root.style.setProperty("--font-family", typography.font_family);
}

interface ThemeProviderProps {
  children: React.ReactNode;
}

// Per-product colour overrides. Visual config only — no operational data.
const PRODUCT_COLOR_OVERRIDES: Record<string, Partial<ThemeColors>> = {
  shreembrain:     { primary: "#7C3AED", primary_light: "#A78BFA", primary_dark: "#5B21B6", sidebar_bg: "#7C3AED", sidebar_active: "#A78BFA" },
  shreemleads:     { primary: "#059669", primary_light: "#34D399", primary_dark: "#065F46", sidebar_bg: "#059669", sidebar_active: "#34D399" },
  shreemads:       { primary: "#DC2626", primary_light: "#F87171", primary_dark: "#991B1B", sidebar_bg: "#DC2626", sidebar_active: "#F87171" },
  shreempulse:     { primary: "#2563EB", primary_light: "#60A5FA", primary_dark: "#1D4ED8", sidebar_bg: "#2563EB", sidebar_active: "#60A5FA" },
  shreemguardrails: { primary: "#D97706", primary_light: "#FCD34D", primary_dark: "#92400E", sidebar_bg: "#D97706", sidebar_active: "#FCD34D" },
};

export function getThemeForProduct(productId: string): ProductTheme {
  const base = platformTheme as ProductTheme;
  const overrides = PRODUCT_COLOR_OVERRIDES[productId];
  if (!overrides) return base;
  return {
    ...base,
    product_id: productId,
    colors: { ...base.colors, ...overrides },
  };
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  useEffect(() => {
    const productId = process.env.NEXT_PUBLIC_PRODUCT_ID ?? "platform";
    injectCssVariables(getThemeForProduct(productId));
  }, []);

  return <>{children}</>;
}
